<!--

Copyright Paul B. Lucas (C) 2025   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when user (as a regular user) selects the 
VIEW MY ASSETS route on the initial splash page.  Assets are listed with significant properties in a table 
row format if the @media probe returns a screen size > MOBILE.  If MOBILE size is detected, then agreement 
'rows' are collapsed.  

Revisions:
    02/26/2025      pblucas     initial creation


-->

<template>
    <div class="_master_container">

        <div v-if="renderState == 'LIST'">
            <div v-if="assets?.length && assets.length != 0">
                <div class="_list">
                    <p class="_p_list_header">Assets for {{ user['name-concat'] }}</p>
                </div>
                <div class="_list_container">
                    <div class="_assets_table">

                        <div class="_assets_table_row">
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Tag</p>
                            </div> 
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Owner Org</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Asset Type</p>
                            </div>
                            <div class="_assets_table_cell_ellipsis">
                                <p class="_p_list_header">Description</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Manufacturer</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Model</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">OS Type</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_actions_header">Actions</p>
                            </div>
                            <div class="_assets_table_cell">
                                &nbsp;
                            </div>


                        </div>
                    
                        <div class="_assets_table_row" v-for="(asset,idx) in assets" :key="assets[idx].uuid">
                            <div class="_employee_table_link_cell" style="text-align: left;"
                            tabindex="0" 
                            aria-label="Clickable link ofr viewing asset"  
                            aria-description="Asset tag as link for click/enter key"
                            @click="viewAsset(asset, idx)"
                            @keydown.enter="viewAsset(asset,idx)">
                                {{ asset['tag'] }}
                            </div>
                            <div class="_assets_table_cell" style="text-align: left;">
                                {{ asset['owner-org'] }}
                            </div>                
                            <div class="_assets_table_cell">
                                {{ prettyType(asset['asset-type']) }}
                            </div>
                            <div class="_assets_table_cell_ellipsis">
                                {{ asset.description }}
                            </div>
                            <div class="_assets_table_cell">
                                {{ asset.mfg }}
                            </div>
                            <div class="_assets_table_cell">
                                {{ asset.model }}
                            </div>
                            <div class="_assets_table_cell">
                                {{ asset['os-type'] }}
                            </div>
                            <div class="_asset_actions_table_cell">
                                <button class="_list_action_button" @click.stop="editAsset(asset, idx)">Edit Asset</button>
                                <button class="_list_action_button" @click.stop="viewAsset(asset, idx)">View Asset</button>
                            </div>
                            <div class="_assets_table_cell">
                                &nbsp;
                            </div>
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
            <div v-else>
                <p class="_assets_warning">
                    You have no assets saved or in effect
                </p>
            </div>      
        </div>      <!-- end of list render -->

        <div v-if="renderState == 'VIEW'">
            <!-- render the ViewAgreementEntire View on selected agreement -->
            <ViewAssetEntire :asset="assets[viewIndex]" 
                             :key="assets[viewIndex].uuid" />
                             
        </div>
    
        <!-- edit the selected asset -->
        <div v-if="renderState == 'EDIT'">
            <EditAsset      :asset="assets[viewIndex]" 
                            :key="assets[viewIndex].uuid" 
                            :restriction="'NONE'"  />
        </div>
 

        <div class="_assets_controls">

            <button class="_main_button" type="button" v-if="renderState != 'LIST'"
                @click="backToList()"
                @keydown.enter="backToList()">Back to List</button>

            <button class="_main_button" type="button"  v-if="assets.length > 1 && (renderState == 'VIEW' || renderState == 'EDIT')"
                @click="nextAsset()"
                @keydown.enter="nextAsset()">Next </button>
            <button class="_main_button"  type="button" v-if="renderState == 'EDIT'"
                @click="cancelEdit()"
                @keydown.enter="cancelEdit()">Cancel </button>
            <button class="_main_button"  type="button" v-if="renderState == 'EDIT'" 
                @click="saveAsset()"
                @keydown.enter="saveAsset()">Save </button>
        </div>
        <!--   breaks to allow footer space -->
        <br/><br/>
    </div>
</template>

<script>
    import {ref, computed}          from 'vue'
    import {useStore}               from 'vuex'

    import ViewAssetEntire          from '@/components/Assets/ViewAssetEntire.vue'
    import EditAsset                from '@/components/Assets/EditAsset.vue'

    //import composables
    import useAssetsAPI             from '@/composables/useAssetsAPI'
    import useDateHelpers           from '@/composables/useDateHelpers'
    import useRefreshAPI            from '@/composables/useRefreshAPI'
    import usePermissionsAPI        from  '@/composables/usePermissionsAPI'
 
    // import enumerations
    import SysConfigs               from '@/configuration/form-options.json'
    
    export default {
        components: {
            ViewAssetEntire,
            EditAsset
        },
        props: [
            'claims',
            'gat'
        ],
        emits: [
        ],
    
        setup(props)    {
           
            const { assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()
            const store = useStore()
            const { checkStoreForRefresh }  = useRefreshAPI()
            const { checkAdminGroups }      = usePermissionsAPI()

            //  store data
            const user                  =   computed( () => store.state.user)

            const sysConfigs            = ref([])
            sysConfigs.value            = SysConfigs
             
            const assets                =   ref([])
            const backupAsset           =   ref({})
            const assetOrigList         =   ref([])         // list of assets found prior to dedub
            const viewIndex             =   ref(0)
  
            // FUNCTION: Support method to dedup assets
            function deDupeAssets(list) {
                let uniqueAssetsList    =   []
                let keyList             =   []
                // original list passed in
                for (let idx in list)  {
                    let assetTag = list[idx]?.tag ?? 0          // asset tag undefined or nul presents 0 
                    if(assetTag == 0) continue                  // ignore null/0 tags
                    assetTag.toUpperCase()                      // cleans up case typos
                    if(!keyList.includes(assetTag)) {
                        // new tag
                        keyList.push(assetTag)
                        uniqueAssetsList.push(list[idx])       
                    }
                    // continue if dup
                }   
                // return deduped list
                return uniqueAssetsList
            }
                
            // MARK:  refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            .then( async () => {
                await fetchAssetsOnQuery({"employee-fk": user.value.id})
                .then( () => {
                    
                    //assets.value = assetResults.value

                    // since we may need to update multiple instances on different agreements,  preserve the 
                    // original fetched asset list
                    assetOrigList.value     = JSON.parse(JSON.stringify(assetResults.value))

                    // deep copy to ref
                    assets.value = JSON.parse(JSON.stringify(deDupeAssets(assetResults.value)))

                    //console.log("ListMyAssets:: assetOrigList(non-dedup): "+JSON.stringify(assetOrigList.value))
                    //console.log("ListMyAssets:: assetOrigList(dedup): "+JSON.stringify(assets.value))
 
                })
            })
            checkAdminGroups(props.claims)

            return {user, assets, backupAsset, assetOrigList, viewIndex, sysConfigs, deDupeAssets}
        },

        data() {
            return {
                renderState: 'LIST'
            }
        },

        methods: {

            // FUNCTION:  checks asset type to see if asset is CI (configuration item)
            // returns true if CI
            // Agrguments are asset and idx to agreements found
            //
             // Function: converts enumeration to more readable 
             prettyType(s) {
                if (!s) return ""
                s = s.replaceAll('_',' ')
                let aW = s.toLowerCase().split(' ')
                for (let i = 0; i < aW.length; i++) {
                    aW[i] = aW[i].charAt(0).toUpperCase() + aW[i].slice(1);
                }
                return aW.join(' ')
            },

            // FUNCITON: set viewIndex to argument index and set rendering swotches
            viewAsset(_, idx)     {
                this.viewIndex      =   idx
                this.renderState    =   'VIEW'
            },  
            // FUNCTION: takes asset from selected row and applies to RWAForm component
            editAsset(asset, idx) {    
                this.backupAsset    =   JSON.parse(JSON.stringify(this.assets[idx]))  
                this.viewIndex      =   idx  
                this.renderState    =   'EDIT'
             },
            // FUNCTION: increments through viewing index 
            nextAsset() {
                this.viewIndex++
                if (this.viewIndex >= this.assets.length) this.viewIndex = 0
            },
            async backToList() {
                const {assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()

                // need to reload list as there may be new copies
                await fetchAssetsOnQuery({'employee-fk': this.user.id})
                .then( () => {
                     this.assetOrigList    = JSON.parse(JSON.stringify(assetResults.value))

                    // deep copy to ref
                    this.assets = JSON.parse(JSON.stringify(this.deDupeAssets(assetResults.value)))

                    this.renderState    =   'LIST'  
                })       
            },
            // FUNCTION to save edited asset
            // Note:    Asset changes may be on one of multiple duplicated assets on other agreement objects.  These
            //          changs must be propagated to other instances by finding all assets with same unique tag.
            //          but preserving fk_employee_id, fk_agreement_id, and uuid.
            //
            async saveAsset() {

                const {assetResults, assetKeyData, fetchAssetsOnQuery, storeAsset} = useAssetsAPI()

                // iterate the original asset list looking for the edited asset and deep copy the attributes
                await storeAsset(this.assets[this.viewIndex])
                .then(async () => {
                    await fetchAssetsOnQuery({id: assetKeyData.value[0].pkey})
                    .then( () => {
                        this.assets[this.viewIndex]     = JSON.parse(JSON.stringify(assetResults.value[0] ))  
                        this.renderState        =   "EDIT"   
                    })
                })    
            },   
            cancelEdit()    {
                this.assets[this.viewIndex] = this.backupAsset
                this.renderState = 'LIST'
            },

            // FUNCTION:  invokes the local machine analyzer on localhost to determine configuration items
            invokeLocalAnalyzer(_, idx) {

                const {assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()
                const {analysisResults, runAnalysis}    =   useCIAnalyzerAPI()

                //const {exec}    = require("child-process")
                //const {isWsl}   = require('is-wsl')
                //const {spawn} = require("cross-spawn")
                

                // pull out the actual asset in the list to be auditednpm run ser4ve

                let subjectAsset = this.assets[idx]

                // set asset properties discovered
                let date = new Date()
                subjectAsset['date-last-audit'] = date.toDateString()

                // determine high level OS type for detailed analysis
                let OSName = null;
                if (navigator.userAgent.indexOf("Win") != -1)   OSName = "Windows";
                if (navigator.userAgent.indexOf("Mac") != -1)   OSName = "MacOS";
                if (navigator.userAgent.indexOf("X11") != -1)   OSName = "UNIX";
                if (navigator.userAgent.indexOf("Linux") != -1) OSName = "Linux";

                // pull OS type from userAgent string via regex
                let OSType = navigator.userAgent.match(/\(([A-Za-z0-9\.\ ]*);/)[1]
                subjectAsset['os-type'] = OSType
                
                // specific functionality per OS
                let systemInfo = ""
                switch(OSName)  {
                    case 'Windows':

                        console.log("Analyzer:: navigator: "+JSON.stringify())
                        break;

                    case 'MacOS':

                        break;

                    case 'UNIX':

                        break;

                    case 'Linux':

                        break;
                }

                //console.log("LMAS:: audit:: asset: "+JSON.stringify(subjectAsset))

                // udate asset
                storeAsset(subjectAsset)
                .then( async () => {
                    await fetchAssetsOnQuery({"employee-fk": this.user.id})
                    .then( () => {
                        this.assets[this.viewIndex]     = JSON.parse(JSON.stringify(assetResults.value[0] ))   
                        this.renderState    =   'LIST'
                        this.viewIndex      =   idx

                    })
                })
            },

            // FUNCTION:  cleans up ISO date string
            prettyDate(date)     {
                const {makePrettyDate}      =   useDateHelpers()
                return makePrettyDate(date)             
                }
        },

        // hooks
        beforeMount() {

        },
        mounted() {

        },
        updated() {
            //console.log("LMAs:: Updated:: Number of My Assets Found: "+this.assets.length)
            //console.log("LMAs:: Updated:: Assets: "+JSON.stringify(this.assets))
        }
    }
</script>

<style scoped>

    ._master_container {
        font-family: Monserrat, Avenir, Helvetica, Arial, sans-serif;
        min-height: 50vh;
 
    }
 
    ._list_container {
        width: 98%;
        overflow-x: auto;
        margin: 0 5px;;
        padding: 0;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #BFC9D9;
    }
    ._list {
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._p_list_header {
        margin: 0;
        padding: 2px 5px;
        color: #13294B;
        font-size: 0.9em;
        font-weight: bold;
        text-align: left;
    }
    ._p_list_actions_header {
        margin: 0 auto;
        padding: 2px 5px;
        color: #13294B;
        font-size: 0.9em;
        font-weight: bold;
        text-align: center;
    }

    /* list action button styling */
    ._list_action_button {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#13294B;
        color: white;
        margin: 0 5px;
        padding: 0 3px;
        font-size: 0.8em;
    }
    ._list_action_button:hover {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#FF5F05;
        color: white;
        margin: 0 5px;
        font-size: 0.8em;
    }
   
    ._assets_controls {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0 5px;
    }

    /* agreements list styling */
    ._assets_table {
        display: table;
    }
    ._assets_table_row   {
        display: table-row;
        background-color:#BFC9D9;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        overflow: hidden;
        height: 25px;
        white-space: nowrap;
    }

    ._assets_table_row:nth-child(2n+2) {
        display: table-row;
        text-align: center;
        margin: 0;
        background-color:#a4b9dc;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
    }
     ._assets_table_cell  {
        display: table-cell;
        width: 10%;
        /* min-width: 40px; */
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px;
    }
    ._employee_table_link_cell  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        color: #13294B;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._employee_table_link_cell:hover  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        color: #FF5F05;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._assets_table_cell_ellipsis  {
        display: table-cell;
        width: 20%;
        /* min-width: 40px; */
        text-align: left;
        text-overflow: ellipsis;
        margin: 0;
        padding: 2px 0 2px 10px;
    }
    ._asset_actions_table_cell {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content:space-between;
        margin: 0 auto;
    }
    ._asset_tag_table_cell {
        display: table-cell;
        width: 15%;
        /*   min-width: 40px;   */
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px; 
    }
    ._asset_tag_table_cell:hover {
        display: table-cell;
        width: 15%;
        /*   min-width: 40px;   */
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px; 
        font-weight: bold;
        color: #13294B;
    }
    ._p_column_headers {
        font-size: 1.0em;
        font-weight: bold;
        color: #13294B;
        text-align: center;
        margin: 2px 0;
        padding: 5px;
    }
 
    ._assets_warning {
        text-align: center;
        color: #FF5F05;
        margin: 25px auto;
        padding: 5px;
    }
 
    /* general controls styling */
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    
    @media screen and (max-width: 800px) {
        ._assets_controls {
            width: 50%;  
            margin: 0;
        }
    }


</style>